import { debounce, every } from 'lodash';
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';

import {
  EWebTreatmentRequestLog,
  type RecentSymptomItem,
  TreatmentRequestLog,
  useDiagnosesByUserV2,
} from '@/entities/diagnosis';
import { useQuickDiagnosisDoctor } from '@/entities/doctor';
import { EMedicalCategoryType } from '@/entities/medical-category';
import { useGetBookingSchedules } from '@/entities/telemedicine';
import { useRefetchUser } from '@/entities/user';
import useUserProfiles from '@/hooks/common/useUserProfiles';
import useWebViewFocused from '@/hooks/common/useWebViewFocused';
import useGetDiagnosisCategories from '@/hooks/telemedicine/useGetDiagnosisCategories';
import useCustomRouter from '@/hooks/useCustomRouter';
import useLogger from '@/hooks/useLogger';
import useModal from '@/hooks/useModal';
import {
  CardPayCompany,
  TreatmentRequestAgreement,
  UserAddress,
  UserPaymentsInfo,
  UserProfile,
  UserProfileRelationType,
  UserProfiles,
} from '@/models/common';
import {
  DayScheduleGroupedByTimePeriod,
  EDiagnosisStep,
  Medicine,
  NonFaceToFaceBookingSchedulesInfo,
  TreatmentRequest,
  TreatmentRequestDoctor,
  TreatmentRequestDoctorDepartment,
  WebDiagnosisTreatmentRequestPageQueries,
} from '@/models/diagnosis';
import { EDoctorFilterType } from '@/models/diagnosis/doctor-filter';
import { NonF2fDoctor } from '@/models/doctor/telemedicine';
import { userState } from '@/recoil/atoms/common/user';
import { NonBenefitService } from '@/services/non-benefit';
import { User } from '@/types/Auth';
import { Client } from '@/types/app';
import { ROUTES } from '@/types/common';
import { EDiagnosisContactType } from '@/types/diagnosis';
import { BookingScheduleTypeVo, DoctorDetail, NonFaceToFaceDiagnosisBookingSchedulesVo } from '@/types/doctor';
import { MODAL_BUTTON_TYPE, MODAL_TYPE } from '@/types/modal';
import { sleep } from '@/utils/date';
import { errorReport } from '@/utils/report';
import { addQueryString, deleteQueryString } from '@/utils/url';
import { TELEMEDICINE_REQUEST_HEADER_TITLES } from '@/views/telemedicine/request/constants';
import useRefreshPrevention from '@/views/telemedicine/request/hooks/useRefreshPrevention';
import { useToast } from 'doctornow-design-system';
import { DOCTOR_SORT_KEY } from '../ui';
import useCategoryGuideBottomSheet from './useCategoryGuideBottomSheet';
import useStepValidation from './useStepValidation';
import { useTelemedicineAccordion } from './useTelemedicineAccordion';
import { useTelemedicineAgreement } from './useTelemedicineAgreement';
import { useTelemedicineDoctorSelection } from './useTelemedicineDoctorSelection';
import { useTelemedicineFormState } from './useTelemedicineFormState';
import { useTelemedicinePayment } from './useTelemedicinePayment';
import { useTelemedicineRequestSubmission } from './useTelemedicineRequestSubmission';
import { useTelemedicineSchedule } from './useTelemedicineSchedule';
import { useTelemedicineSsn } from './useTelemedicineSsn';
import { useTelemedicineSymptom } from './useTelemedicineSymptom';

export interface AccordionActions {
  openSection: (options?: { scroll?: boolean }) => void;
  closeSection: () => void;
  toggleSection: () => void;
}

export enum CardCompanyButtonType {
  KAKAOPAY = 'KAKAOPAY',
  ANOTHER = 'ANOTHER',
}

export enum AccordionState {
  Days = 'days',
  IsVisitBrand = 'isVisitBrand',
  Ssn = 'ssn',
  ChildInfo = 'childInfo',
  DetailSymptom = 'detailSymptom',
  RequestCallType = 'requestCallType',
}

const chronicDiseaseCategoryIds: readonly string[] = [
  '607', // 고혈압
  '570', // 고지혈증
  '569', // 당뇨
  '611', // 통풍
];

const DEFAULT_CHRONIC_DISEASE_CATEGORY_WARNING_MESSAGES = [
  '병원에서 진행한 혈액 검사 결과가 있다면 더 좋아요.',
  '복용 중인 약의 이름을 입력해주세요.',
  '약이나 약 봉투 또는 처방전을 찍어 함께 올려주세요.',
];

const chronicDiseaseCategoryWarningMessageMap: Record<string, readonly string[]> = {
  '607': ['가장 최근 측정한 혈압 수치를 입력해주세요.', ...DEFAULT_CHRONIC_DISEASE_CATEGORY_WARNING_MESSAGES],
  '570': ['가장 최근 측정한 콜레스테롤 수치를 입력해주세요.', ...DEFAULT_CHRONIC_DISEASE_CATEGORY_WARNING_MESSAGES],
  '569': [
    '가장 최근 측정한 당화혈색소, 공복 혈당을 입력해주세요.',
    ...DEFAULT_CHRONIC_DISEASE_CATEGORY_WARNING_MESSAGES,
  ],
  '611': ['가장 최근 측정한 요산 수치를 입력해주세요.', ...DEFAULT_CHRONIC_DISEASE_CATEGORY_WARNING_MESSAGES],
};

const chronicDiseaseCategoryPlaceholderMap: Record<string, string> = {
  '607': '복용 중인 약, 혈압 수치를 입력해주세요',
  '570': '복용 중인 약, 콜레스테롤 수치를 입력해주세요',
  '569': '복용 중인 약, 당화혈색소, 공복 혈당을 입력해주세요',
  '611': '복용 중인 약, 요산 수치를 입력해주세요',
};

export interface UseTelemedicineRequestViewModel {
  accordionRefs: Record<AccordionState, RefObject<AccordionActions>>;
  recentlyDetailSymptomsCount: number;
  isUserPaymentsInfoRefetching: boolean;
  isUserPaymentsInfoFetched: boolean;
  ssnInputRef: RefObject<HTMLInputElement>;
  detailSymptomInputRef: RefObject<HTMLTextAreaElement>;
  reVisitBrandWrapperRef: RefObject<HTMLDivElement>;
  cardListWrapperRef: RefObject<HTMLDivElement>;
  isRequestDiagnosisLoading: boolean;
  isPreSignedUrlsLoading: boolean;
  step: EDiagnosisStep;
  categoryId: string | undefined;
  type: EMedicalCategoryType | undefined;
  treatmentRequest: TreatmentRequest;
  bookingSchedulesInfo: NonFaceToFaceBookingSchedulesInfo;
  selectedTimeSlotTitle: string;
  userPaymentsInfo: UserPaymentsInfo;
  userProfiles: UserProfiles;
  userTargetId: string;
  point: number;
  cardCompanyButton: CardCompanyButtonType | undefined;
  requestAgreementList: TreatmentRequestAgreement[];
  doctorPublicId: string | null;
  user: User | null | undefined;
  updatePatientSsn: (ssn: string) => void;
  selectedDay: DayScheduleGroupedByTimePeriod | null;
  selectedDayScheduleLength: number;
  isChronicDiseaseCategory: boolean;
  chronicDiseaseCategoryWarningMessages: readonly string[];
  chronicDiseaseCategoryPlaceholder: string;
  isCardInstallmentBottomSheetOpen: boolean;
  isRadioBottomSheetOpen: boolean;
  recentlyDetailSymptoms: RecentSymptomItem[];
  searchDoctors: NonF2fDoctor[] | undefined;
  isLoadingSearchDoctors: boolean;
  hasNextPageSearchDoctors: boolean;
  isSearchDoctorSortFilterBottomSheetOpen: boolean;
  isRequiredDataFetched: boolean;
  isDoctorStepButtonDisabled: boolean;
  isEnabledScheduleAlertOpen: boolean;
  selectedStartDisplayTimeSlot: string;
  selectedDoctorDetail: DoctorDetail | null;
  selectMedicine: (item: Medicine) => void;
  updateInstallment: (installmentMonth: number) => void;
  selectCard: (cardId: string) => void;
  toggleRequestUsePoint: () => void;
  updateSymptom: (symptom: string) => void;
  resetPatientSsn: () => void;
  selectBookingTargetDate: (date: string) => void;
  selectBookingTimeSlot: (startTimeSlotValue: number, endTimeSlotValue: number) => Promise<void>;
  toggleAsapRequest: () => void;
  selectQuickMode: () => Promise<void>;
  selectGeneralMode: () => void;
  toggleQuickScheduleMode: () => void;
  selectAddress: (selectedAddress: UserAddress) => void;
  onHandleChangeSymptomImage: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  removeSymptomImage: (index: number) => void;
  nextStep: (step: EDiagnosisStep, currentCategoryId?: string, currentType?: EMedicalCategoryType) => void;
  handleDiagnosisStepNext: (_event?: React.MouseEvent<HTMLButtonElement>) => Promise<void>;
  getDiagnosisStepButtonText: () => string;
  selectCardCompanyButton: (buttonType: CardCompanyButtonType) => void;
  openCardInstallmentListBottomSheet: () => void;
  toggleTreatmentRequestAgreement: (selectedTerm: TreatmentRequestAgreement) => void;
  toggleTreatmentRequestAllAgreement: () => void;
  moveToAgreementLink: (url: string) => void;
  scrollToBottomAndCheckAllAgreement: () => void;
  moveToBack: () => Promise<void>;
  addCreditCard: () => void;
  addKakaopay: () => void;
  openDetailSymptomsLoadBottomSheet: () => void;
  requestCameraPermission: () => Promise<boolean>;
  scrollToBottom: () => void;
  getHeaderTitle: () => string | undefined;
  shouldShowQuickDiagnosisInfoBanner: () => boolean;
  handleCategorySelectDepartment: (department: TreatmentRequestDoctorDepartment) => void;
  handleMedicineSelect: (item: Medicine) => void;
  closeCardInstallmentBottomSheet: () => void;
  handleInstallmentSelect: (item: { installmentMonth: number; text: string }) => void;
  hasDetailMedicine: (treatmentMedicineId: string | null) => boolean;
  closeRadioBottomSheet: () => void;
  handleSymptomSelect: (selectedSymptom: RecentSymptomItem) => void;
  getCategoryName: () => string;
  fetchNextPageSearchDoctors: () => void;
  openSearchDoctorSortFilterBottomSheet: () => void;
  closeSearchDoctorSortFilterBottomSheet: () => void;
  handleSearchDoctorSortFilterSelect: (sortKey: DOCTOR_SORT_KEY) => Promise<void>;
  handleSearchDoctorSpecialistOnlySelect: (isSpecialistOnly: boolean) => void;
  getUserProfile: () => UserProfile | null;
  onOpenDoctorDetailBottomSheet: (doctorPublicId: string) => void;
  getDoctorDetailOpenCount: () => number;
  onCloseDoctorDetailBottomSheet: () => void;
  getDoctorIdByQueryString: () => string | null;
  onOpenRegisterCardBottomSheet: () => void;
  onCloseRegisterCardBottomSheet: () => void;
  getIsGeneralCardRegister: () => boolean;
  onSuccessCardRegister: () => Promise<void>;
  onCertificationSuccess: () => Promise<void>;
  shouldShowDiagnosisFormStep: () => boolean;
  shouldShowDoctorSelectStep: () => boolean;
  shouldShowPaymentStep: () => boolean;
  shouldShowCertificationGuideStep: () => boolean;
  shouldShowDoctorFilterStep: () => boolean;
  handleSelectDoctor: (doctor?: TreatmentRequestDoctor, doctorPublicId?: string, doctorDetail?: DoctorDetail) => void;
  onCloseIsEnabledScheduleAlert: () => void;
  selectedDoctorFilters: EDoctorFilterType[];
  handleToggleDoctorFilter: (filterType: EDoctorFilterType) => void;
  handleDoctorFilterSelectionNext: () => void;
  onSubmitRequestDiagnosis: () => void;
  isMaternityGuideBottomSheetOpen: boolean;
  openMaternityGuideBottomSheet: () => void;
  closeMaternityGuideBottomSheet: () => void;
  isMaleGuideBottomSheetOpen: boolean;
  openMaleGuideBottomSheet: () => void;
  closeMaleGuideBottomSheet: () => void;
  isMentalHealthGuideBottomSheetOpen: boolean;
  openMentalHealthGuideBottomSheet: () => void;
  closeMentalHealthGuideBottomSheet: () => void;
  isDietGuideBottomSheetOpen: boolean;
  openDietGuideBottomSheet: () => void;
  closeDietGuideBottomSheet: () => void;
  isSleepDisorderGuideBottomSheetOpen: boolean;
  openSleepDisorderGuideBottomSheet: () => void;
  closeSleepDisorderGuideBottomSheet: () => void;
}

const useTelemedicineRequestViewModel = (client: Client): UseTelemedicineRequestViewModel => {
  const logger = useLogger();
  const isFocused = useWebViewFocused();
  const { toast } = useToast();
  const { showModal, closeModal } = useModal();
  const { router } = useCustomRouter();
  const user = useRecoilValue(userState);
  const { refetchUser } = useRefetchUser();
  const { getQuickDiagnosisDoctorInfo } = useQuickDiagnosisDoctor();

  const queries = router.query as unknown as WebDiagnosisTreatmentRequestPageQueries;
  const {
    type,
    categoryId,
    userTargetId,
    step = EDiagnosisStep.Diagnosis,
    targetDate = '',
    contactType = EDiagnosisContactType.NonF2F,
  } = queries;

  const detailSymptomInputRef = useRef<HTMLTextAreaElement>(null);
  const reVisitBrandWrapperRef = useRef<HTMLDivElement>(null);
  const cardListWrapperRef = useRef<HTMLDivElement>(null);

  const { userProfiles, isUserProfilesFetched } = useUserProfiles();
  const { recentlyDetailSymptoms, recentlyDetailSymptomsCount } = useDiagnosesByUserV2({ size: 5 });
  const { categoriesInfo } = useGetDiagnosisCategories();
  const { bookingSchedulesInfo, isBookingSchedulesFetched } = useGetBookingSchedules();

  const [isCardInstallmentBottomSheetOpen, setIsCardInstallmentBottomSheetOpen] = useState(false);
  const [isRadioBottomSheetOpen, setIsRadioBottomSheetOpen] = useState(false);
  const [selectedDoctorDetail, setSelectedDoctorPublicId] = useState<DoctorDetail | null>(null);
  const [isEnabledScheduleAlertOpen, setIsEnabledScheduleAlertOpen] = useState(false);
  const [selectedStartDisplayTimeSlot, setSelectedStartDisplayTimeSlot] = useState<string>('');

  const { categoryGuideBottomSheetStates, categoryGuideBottomSheetActions, categoryGuideBottomSheetOpenActionMap } =
    useCategoryGuideBottomSheet();

  const formState = useTelemedicineFormState({ queries });
  const { treatmentRequest, updateForm, updateFormFromTreatmentRequest } = formState;

  const accordion = useTelemedicineAccordion();
  const { accordionRefs } = accordion;

  const { ssnInputRef, updatePatientSsn, resetPatientSsn, focusSsnInput } = useTelemedicineSsn({
    treatmentRequest,
    updateForm,
    accordionRefs,
    hasVideoRequestCallType: treatmentRequest.form.hasVideoRequestCallType,
    detailSymptomInputRef,
  });

  const {
    isPreSignedUrlsLoading,
    updateSymptom,
    removeSymptomImage,
    onHandleChangeSymptomImage,
    handleMedicineSelect,
  } = useTelemedicineSymptom({
    treatmentRequest,
    updateForm,
    detailSymptomInputRef,
    userTargetId,
  });

  const {
    selectedDay,
    selectedDayScheduleLength,
    selectBookingTargetDate,
    selectBookingTimeSlot,
    toggleAsapRequest,
    selectQuickMode,
    selectGeneralMode,
    toggleQuickScheduleMode,
    selectInitSchedule,
  } = useTelemedicineSchedule({
    treatmentRequest,
    bookingSchedulesInfo,
    updateFormFromTreatmentRequest,
    accordionRefs,
    focusSsnInput,
  });

  const {
    point,
    cardCompanyButton,
    userPaymentsInfo,
    isUserPaymentsInfoRefetching,
    isUserPaymentsInfoFetched,
    selectCard,
    updateInstallment,
    toggleRequestUsePoint,
    selectCardCompanyButton,
    selectAddress,
    addKakaopay,
    refetchUserPayments,
    selectInitCreditCard,
  } = useTelemedicinePayment({
    treatmentRequest,
    updateForm,
    client,
  });

  const {
    doctorPublicId,
    setDoctorPublicId,
    searchDoctors,
    isLoadingSearchDoctors,
    hasNextPageSearchDoctors,
    isSearchDoctorSortFilterBottomSheetOpen,
    fetchNextPageSearchDoctors,
    openSearchDoctorSortFilterBottomSheet,
    closeSearchDoctorSortFilterBottomSheet,
    handleSearchDoctorSortFilterSelect,
    handleSearchDoctorSpecialistOnlySelect,
    onOpenDoctorDetailBottomSheet,
    getDoctorDetailOpenCount,
    onCloseDoctorDetailBottomSheet,
    getDoctorIdByQueryString,
    onSelectDoctor,
    isDoctorStepButtonDisabled,
    selectedFilters,
    handleToggleFilter,
    handleFilterSelectionNext,
  } = useTelemedicineDoctorSelection({
    queries,
    treatmentRequest,
    updateFormFromTreatmentRequest,
    step,
  });

  const {
    requestAgreementList,
    toggleTreatmentRequestAgreement,
    toggleTreatmentRequestAllAgreement,
    scrollToBottomAndCheckAllAgreement,
    scrollToBottom,
  } = useTelemedicineAgreement({
    treatmentRequest,
    contactType,
    updateForm,
    client,
  });

  const { requestDiagnosis, isRequestDiagnosisLoading } = useTelemedicineRequestSubmission({
    bookingSchedulesInfo,
    categoriesInfo,
  });

  const onCertificationSuccess = useCallback(async () => {
    await refetchUser();

    if (!treatmentRequest.form.doctorPid) {
      return;
    }

    requestDiagnosis({
      skipCertificationCheck: true,
      treatmentRequest,
      isUserNeedVerificationForDiagnosis: user?.isNeedVerificationForDiagnosis ?? false,
    });
  }, [refetchUser, treatmentRequest, user, requestDiagnosis]);

  const isRequiredDataFetched = useMemo(() => {
    return every([isUserPaymentsInfoFetched, isBookingSchedulesFetched, isUserProfilesFetched]);
  }, [isUserPaymentsInfoFetched, isBookingSchedulesFetched, isUserProfilesFetched]);

  const isChronicDiseaseCategory = chronicDiseaseCategoryIds.includes(treatmentRequest.form.categoryId ?? '');

  const chronicDiseaseCategoryWarningMessages =
    chronicDiseaseCategoryWarningMessageMap[treatmentRequest.form.categoryId ?? ''] ?? [];

  const chronicDiseaseCategoryPlaceholder =
    chronicDiseaseCategoryPlaceholderMap[treatmentRequest.form.categoryId ?? ''];

  const getHeaderTitle = () => {
    return TELEMEDICINE_REQUEST_HEADER_TITLES[step];
  };

  const getCategoryName = useCallback(() => {
    const categoryType = type;
    return categoriesInfo.getCategoryNameByIdAndType(categoryId, categoryType);
  }, [type, categoryId, categoriesInfo]);

  const requestCameraPermission = async () => {
    return true;
  };

  const hasDetailMedicine = (treatmentMedicineId: string | null) => {
    return NonBenefitService.hasDetailMedicineTreatment(treatmentMedicineId);
  };

  const exitTreatmentRequestPage = useCallback(() => {
    showModal({
      type: MODAL_TYPE.ALERT,
      props: {
        title: '진료 신청을 그만둘까요?',
        message: '작성 중이던 내용은 저장되지 않아요.',
        buttons: [
          {
            type: MODAL_BUTTON_TYPE.DEFAULT,
            label: '그만두기',
            onClick: () => {
              logger.log({
                eventName: TreatmentRequestLog.RequestClickStopInModal,
              });

              router.back();
              closeModal();
            },
          },
          {
            type: MODAL_BUTTON_TYPE.PRIMARY,
            label: '계속 작성',
            onClick: () => {
              logger.log({
                eventName: TreatmentRequestLog.RequestClickContinueInModal,
              });
              closeModal();
            },
          },
        ],
        onClickOverlay: () => {
          closeModal();
        },
      },
    });
  }, [showModal, logger, client.isWebView, router, closeModal]);

  const moveToBack = useCallback(async () => {
    if (step === EDiagnosisStep.Diagnosis) {
      logger.log({
        eventName: TreatmentRequestLog.RequestPrevStepFromDiagnosis,
      });
      exitTreatmentRequestPage();
      return;
    }

    if (step === EDiagnosisStep.DoctorFilter) {
      logger.log({
        eventName: TreatmentRequestLog.RequestPrevStepFromDiagnosis,
      });
    }

    if (step === EDiagnosisStep.Payment) {
      logger.log({
        eventName: TreatmentRequestLog.RequestPrevStepFromPayment,
      });
      setDoctorPublicId(null);
    }

    if (step === EDiagnosisStep.CertificationGuide) {
      logger.log({
        eventName: EWebTreatmentRequestLog.ClickIdvSheetClose,
      });
    }

    router.back();

    await sleep(100);
  }, [step, logger, router, setDoctorPublicId, exitTreatmentRequestPage]);

  const openDetailSymptomsLoadBottomSheet = () => {
    if (recentlyDetailSymptoms.length === 0) {
      toast({ text: '최근 진료가 없어 증상을 불러올 수 없어요.', direction: 'bottom' });
      return;
    }

    setIsRadioBottomSheetOpen(true);
  };

  const closeRadioBottomSheet = () => {
    setIsRadioBottomSheetOpen(false);
  };

  const handleSymptomSelect = (selectedSymptom: RecentSymptomItem) => {
    logger.log({
      eventName: TreatmentRequestLog.RequestSelectLoadDetailSymptom,
    });
    updateSymptom(selectedSymptom.symptom);
    closeRadioBottomSheet();
  };

  const openCardInstallmentListBottomSheet = () => {
    setIsCardInstallmentBottomSheetOpen(true);
  };

  const closeCardInstallmentBottomSheet = () => {
    setIsCardInstallmentBottomSheetOpen(false);
  };

  const handleInstallmentSelect = (item: { installmentMonth: number; text: string }) => {
    updateInstallment(item.installmentMonth);
    closeCardInstallmentBottomSheet();
  };

  const nextStep = useCallback(
    debounce(
      (step: EDiagnosisStep, currentCategoryId?: string, currentType?: EMedicalCategoryType) => {
        router.push(
          `${ROUTES.TELEMEDICINE.REQUEST.path}` +
            addQueryString({
              ...router.query,
              step,
              categoryId: currentCategoryId,
              type: currentType,
            })
        );
      },
      300,
      {
        leading: true,
        trailing: false,
      }
    ),
    [categoryId, type]
  );

  const updateQuickDoctorInfo = async (): Promise<boolean> => {
    if (!type || !categoryId) {
      toast({ text: '진료 카테고리 정보가 없습니다.', direction: 'bottom' });
      return false;
    }

    try {
      const quickDoctorInfo = await getQuickDiagnosisDoctorInfo({
        medicalCategory: {
          type: type,
          id: categoryId,
        },
        contactType: contactType,
      });

      if (!quickDoctorInfo.doctorId) {
        toast({ text: '빠른 진료가 가능한 의사를 찾을 수 없습니다.', direction: 'bottom' });
        return false;
      }

      updateForm((prev) => {
        const updated = {
          ...prev,
          isAsapRequest: true,
          doctorId: quickDoctorInfo.doctorId,
          doctorPid: quickDoctorInfo.doctorPid,
          bookingTargetDate: quickDoctorInfo.targetDate,
          startTimeSlotValue: quickDoctorInfo.startTimeSlotValue,
          endTimeSlotValue: quickDoctorInfo.endTimeSlotValue,
        };
        return updated;
      });

      return true;
    } catch (error: unknown) {
      if (error instanceof Error) {
        errorReport('빠른 진료 예약 중 오류 발생', {}, error);
      }

      toast({ text: '빠른 진료 예약에 실패했습니다. 일반 예약을 이용해주세요.', direction: 'bottom' });
      return false;
    }
  };

  const handleDiagnosisStepNext = async (_event?: React.MouseEvent<HTMLButtonElement>) => {
    if (treatmentRequest.isWebQuickScheduleRequest()) {
      const isSuccess = await updateQuickDoctorInfo();

      if (isSuccess) {
        nextStep(EDiagnosisStep.Payment, categoryId, type);
      }

      return;
    }

    nextStep(EDiagnosisStep.DoctorFilter, categoryId, type);
  };

  const getDiagnosisStepButtonText = () => {
    return treatmentRequest.isWebQuickScheduleRequest() ? '다음' : '의사 선택하기';
  };

  const addCreditCard = () => {
    onOpenRegisterCardBottomSheet();
  };

  const onSuccessCardRegister = async () => {
    onCloseRegisterCardBottomSheet();
    const result = await refetchUserPayments();

    if (result.data) {
      const firstPayment = result.data.payments[0];

      if (firstPayment) {
        selectCard(firstPayment.id);

        if (firstPayment.payCompany === CardPayCompany.Kakaopay) {
          selectCardCompanyButton(CardCompanyButtonType.KAKAOPAY);
        } else {
          selectCardCompanyButton(CardCompanyButtonType.ANOTHER);
        }
      }
    }
  };

  const updateUserProfile = useCallback(
    (userProfile: UserProfile) => {
      treatmentRequest.updateUserProfile({
        isSsnChanged: userProfile.isSsnChanged,
        originalPatientBirth: userProfile.birthDate,
        patientRelation: userProfile.relation,
        patientBirth: userProfile.displayedBirthDate,
        targetId: userProfile.targetId,
      });

      if (userProfile.relation === UserProfileRelationType.Children) {
        updateForm((prev) => ({
          ...prev,
          patient: {
            ...prev.patient,
            patientAge: userProfile.age,
          },
        }));
      } else {
        updateFormFromTreatmentRequest(treatmentRequest);
      }
    },
    [treatmentRequest, updateForm, updateFormFromTreatmentRequest]
  );

  const moveToAgreementLink = (url: string) => {
    window.open(url);
  };

  const shouldShowQuickDiagnosisInfoBanner = () => {
    return treatmentRequest.isQuickMatchRequest();
  };

  const getUserProfile = () => {
    return userProfiles.getUserProfileByTargetId(userTargetId);
  };

  const onOpenRegisterCardBottomSheet = () => {
    router.push(
      `${ROUTES.TELEMEDICINE.REQUEST.path}` +
        addQueryString({
          ...router.query,
          cardRegister: 'general',
        })
    );
  };

  const onCloseRegisterCardBottomSheet = () => {
    router.back();
  };

  const getIsGeneralCardRegister = () => {
    return router.query.cardRegister === 'general';
  };

  const handleCategorySelectDepartment = (department: TreatmentRequestDoctorDepartment) => {
    logger.log({
      eventName: TreatmentRequestLog.RequestSelectCategory,
      payload: {
        type: 'department',
        categoryId: department.id,
      },
    });
    treatmentRequest.selectTreatmentRequestDepartment(department);
    updateFormFromTreatmentRequest(treatmentRequest);
    detailSymptomInputRef.current?.scroll();
    detailSymptomInputRef.current?.focus();
  };

  const shouldShowDiagnosisFormStep = () => {
    return step === EDiagnosisStep.Diagnosis;
  };

  const shouldShowDoctorFilterStep = () => {
    return step === EDiagnosisStep.DoctorFilter;
  };

  const shouldShowDoctorSelectStep = () => {
    return step === EDiagnosisStep.Doctor;
  };

  const shouldShowPaymentStep = () => {
    return step === EDiagnosisStep.Payment;
  };

  const shouldShowCertificationGuideStep = () => {
    return step === EDiagnosisStep.CertificationGuide;
  };

  const selectedTimeSlotTitle = useMemo(() => {
    const shouldShowQuickModeTitle = true;
    return bookingSchedulesInfo.getSelectedTimeSlotTitle(treatmentRequest, shouldShowQuickModeTitle);
  }, [bookingSchedulesInfo, treatmentRequest]);

  const getIsEnabledSchedule = (diagnosisBookingSchedules?: NonFaceToFaceDiagnosisBookingSchedulesVo[]) => {
    if (!diagnosisBookingSchedules || diagnosisBookingSchedules.length === 0) {
      return false;
    }

    const { bookingTargetDate, startTimeSlotValue, endTimeSlotValue } = treatmentRequest.form;

    const matchingDateSchedule = diagnosisBookingSchedules.find(
      (schedule) => schedule.targetDate === bookingTargetDate
    );
    if (!matchingDateSchedule) {
      return false;
    }

    const matchingTimeSlot = matchingDateSchedule.schedules.find(
      (slot) => slot.startTimeSlot.value === startTimeSlotValue && slot.endTimeSlot.value === endTimeSlotValue
    );

    if (!matchingTimeSlot) {
      return false;
    }

    setSelectedStartDisplayTimeSlot(matchingTimeSlot.startTimeSlot.displayTime);

    return matchingTimeSlot.bookingStatus === BookingScheduleTypeVo.Bookable;
  };

  const handleSelectDoctor = (
    doctor?: TreatmentRequestDoctor,
    doctorPublicId?: string,
    doctorDetail?: DoctorDetail
  ) => {
    setSelectedDoctorPublicId(doctorDetail ?? null);
    const isEnabledSchedule = getIsEnabledSchedule(doctorDetail?.diagnosisBookingSchedules);
    if (!isEnabledSchedule) {
      setIsEnabledScheduleAlertOpen(true);
      return;
    }
    onSelectDoctor(doctor, doctorPublicId);
  };

  const onCloseIsEnabledScheduleAlert = () => {
    deleteQueryString('doctorId');
    setIsEnabledScheduleAlertOpen(false);
  };

  const getReceiptLocationInfo = () => {
    if (!navigator.geolocation) {
      return;
    }
    navigator.geolocation.getCurrentPosition(({ coords }) => {
      treatmentRequest.updateReceiptLocation({
        latitude: coords.latitude,
        longitude: coords.longitude,
      });
      updateFormFromTreatmentRequest(treatmentRequest);
    });
  };

  const onSubmitRequestDiagnosis = () => {
    requestDiagnosis({
      treatmentRequest,
      isUserNeedVerificationForDiagnosis: user?.isNeedVerificationForDiagnosis ?? false,
    });
  };

  useEffect(() => {
    getReceiptLocationInfo();
  }, []);

  useEffect(() => {
    if (isFocused) {
      Promise.allSettled([refetchUserPayments()]);
    }
  }, [isFocused, refetchUserPayments]);

  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (!isRequiredDataFetched || isInitializedRef.current) {
      return;
    }

    if (bookingSchedulesInfo.daySchedules.length) {
      selectInitSchedule(targetDate);
    }

    if (userProfiles.hasUserProfiles()) {
      const userProfile = userProfiles.getUserProfileByTargetId(userTargetId);

      if (userProfile) {
        updateUserProfile(userProfile);
      }
    }

    if (!treatmentRequest.form.paymentId) {
      selectInitCreditCard(userPaymentsInfo);
    }

    isInitializedRef.current = true;
  }, [
    isRequiredDataFetched,
    targetDate,
    userTargetId,
    bookingSchedulesInfo,
    userProfiles,
    treatmentRequest,
    userPaymentsInfo,
    selectInitSchedule,
    selectInitCreditCard,
    updateUserProfile,
  ]);

  useStepValidation({
    step,
    treatmentRequest,
    isRequiredDataFetched,
  });

  useRefreshPrevention({
    enabled: true,
    skipReload: true,
    modalTitle: '페이지를 새로고침하시겠어요?',
    modalMessage: '새로고침하면 작성 중이던 내용이 사라져요.',
    onCancelText: '새로고침',
    onConfirmText: '계속작성',
    onCancel: () => {
      logger.log({
        eventName: TreatmentRequestLog.RequestClickStopInModal,
        payload: {
          contactType: contactType,
        },
      });
      router.replace(
        ROUTES.TELEMEDICINE.REQUEST.path +
          addQueryString({ step: EDiagnosisStep.Diagnosis, categoryId: categoryId, type: type })
      );
    },
    onConfirm: () => {
      logger.log({
        eventName: TreatmentRequestLog.RequestClickContinueInModal,
        payload: {
          contactType: contactType,
        },
      });
    },
  });

  useEffect(() => {
    if (!categoryId || !type || !isRequiredDataFetched) {
      return;
    }

    const mapKey = `${categoryId}:${type}`;
    const bottomSheetGuideOpenAction = categoryGuideBottomSheetOpenActionMap[mapKey];
    if (bottomSheetGuideOpenAction) {
      bottomSheetGuideOpenAction();
    }
  }, [categoryId, type, isRequiredDataFetched, categoryGuideBottomSheetOpenActionMap]);

  useEffect(() => {
    const stepEventMap = {
      [EDiagnosisStep.Diagnosis]: EWebTreatmentRequestLog.RequestStepChangeToDiagnosis,
      [EDiagnosisStep.DoctorFilter]: EWebTreatmentRequestLog.RequestStepChangeToDoctor,
      [EDiagnosisStep.Doctor]: EWebTreatmentRequestLog.RequestStepChangeToDoctor,
      [EDiagnosisStep.Payment]: EWebTreatmentRequestLog.RequestStepChangeToPayment,
      [EDiagnosisStep.CertificationGuide]: EWebTreatmentRequestLog.RequestStepChangeToCertificationGuide,
    };

    const stepPageMap = {
      [EDiagnosisStep.Diagnosis]: 'diagnosis',
      [EDiagnosisStep.DoctorFilter]: 'doctor_filter',
      [EDiagnosisStep.Doctor]: 'doctor',
      [EDiagnosisStep.Payment]: 'payment',
      [EDiagnosisStep.CertificationGuide]: 'certification_guide',
    };

    logger.log({
      eventName: stepEventMap[step],
      payload: {
        step: stepPageMap[step],
        categoryId: categoryId ?? '',
        categoryName: getCategoryName(),
      },
    });
  }, [step]);

  return {
    accordionRefs,
    recentlyDetailSymptomsCount,
    isUserPaymentsInfoRefetching,
    isUserPaymentsInfoFetched,
    ssnInputRef,
    detailSymptomInputRef,
    reVisitBrandWrapperRef,
    cardListWrapperRef: cardListWrapperRef,
    isRequestDiagnosisLoading,
    isPreSignedUrlsLoading,
    step,
    categoryId,
    type,
    treatmentRequest,
    bookingSchedulesInfo,
    selectedTimeSlotTitle,
    userPaymentsInfo,
    userProfiles,
    userTargetId,
    point,
    cardCompanyButton,
    requestAgreementList,
    doctorPublicId,
    user,
    updatePatientSsn,
    selectedDay,
    selectedDayScheduleLength,
    isChronicDiseaseCategory,
    chronicDiseaseCategoryWarningMessages,
    chronicDiseaseCategoryPlaceholder,
    isCardInstallmentBottomSheetOpen,
    isRadioBottomSheetOpen,
    recentlyDetailSymptoms,
    searchDoctors,
    isLoadingSearchDoctors,
    hasNextPageSearchDoctors,
    isSearchDoctorSortFilterBottomSheetOpen,
    isRequiredDataFetched,
    nextStep,
    isDoctorStepButtonDisabled,
    isEnabledScheduleAlertOpen,
    selectedStartDisplayTimeSlot,
    selectedDoctorDetail,
    selectMedicine: handleMedicineSelect,
    updateInstallment,
    selectCard,
    toggleRequestUsePoint,
    updateSymptom,
    resetPatientSsn,
    selectBookingTargetDate,
    selectBookingTimeSlot,
    toggleAsapRequest,
    selectQuickMode,
    selectGeneralMode,
    toggleQuickScheduleMode,
    selectAddress,
    onHandleChangeSymptomImage,
    removeSymptomImage,
    handleDiagnosisStepNext,
    getDiagnosisStepButtonText,
    selectCardCompanyButton,
    openCardInstallmentListBottomSheet,
    toggleTreatmentRequestAgreement,
    toggleTreatmentRequestAllAgreement,
    moveToAgreementLink,
    scrollToBottomAndCheckAllAgreement,
    moveToBack,
    addCreditCard,
    addKakaopay,
    openDetailSymptomsLoadBottomSheet,
    requestCameraPermission,
    scrollToBottom,
    getHeaderTitle,
    shouldShowQuickDiagnosisInfoBanner,
    handleCategorySelectDepartment,
    handleMedicineSelect,
    closeCardInstallmentBottomSheet,
    handleInstallmentSelect,
    hasDetailMedicine,
    closeRadioBottomSheet,
    handleSymptomSelect,
    getCategoryName,
    fetchNextPageSearchDoctors,
    openSearchDoctorSortFilterBottomSheet,
    closeSearchDoctorSortFilterBottomSheet,
    handleSearchDoctorSortFilterSelect,
    handleSearchDoctorSpecialistOnlySelect,
    getUserProfile,
    onOpenDoctorDetailBottomSheet,
    getDoctorDetailOpenCount,
    onCloseDoctorDetailBottomSheet,
    getDoctorIdByQueryString,
    onOpenRegisterCardBottomSheet,
    onCloseRegisterCardBottomSheet,
    getIsGeneralCardRegister,
    onSuccessCardRegister,
    onCertificationSuccess,
    shouldShowDiagnosisFormStep,
    shouldShowDoctorSelectStep,
    shouldShowPaymentStep,
    shouldShowCertificationGuideStep,
    shouldShowDoctorFilterStep,
    handleSelectDoctor,
    onCloseIsEnabledScheduleAlert,
    selectedDoctorFilters: selectedFilters,
    handleToggleDoctorFilter: handleToggleFilter,
    handleDoctorFilterSelectionNext: handleFilterSelectionNext,
    onSubmitRequestDiagnosis,
    isMaternityGuideBottomSheetOpen: categoryGuideBottomSheetStates.isMaternityGuideBottomSheetOpen,
    openMaternityGuideBottomSheet: categoryGuideBottomSheetActions.openMaternityGuideBottomSheet,
    closeMaternityGuideBottomSheet: categoryGuideBottomSheetActions.closeMaternityGuideBottomSheet,
    isMaleGuideBottomSheetOpen: categoryGuideBottomSheetStates.isMaleGuideBottomSheetOpen,
    openMaleGuideBottomSheet: categoryGuideBottomSheetActions.openMaleGuideBottomSheet,
    closeMaleGuideBottomSheet: categoryGuideBottomSheetActions.closeMaleGuideBottomSheet,
    isMentalHealthGuideBottomSheetOpen: categoryGuideBottomSheetStates.isMentalHealthGuideBottomSheetOpen,
    openMentalHealthGuideBottomSheet: categoryGuideBottomSheetActions.openMentalHealthGuideBottomSheet,
    closeMentalHealthGuideBottomSheet: categoryGuideBottomSheetActions.closeMentalHealthGuideBottomSheet,
    isDietGuideBottomSheetOpen: categoryGuideBottomSheetStates.isDietGuideBottomSheetOpen,
    openDietGuideBottomSheet: categoryGuideBottomSheetActions.openDietGuideBottomSheet,
    closeDietGuideBottomSheet: categoryGuideBottomSheetActions.closeDietGuideBottomSheet,
    isSleepDisorderGuideBottomSheetOpen: categoryGuideBottomSheetStates.isSleepDisorderGuideBottomSheetOpen,
    openSleepDisorderGuideBottomSheet: categoryGuideBottomSheetActions.openSleepDisorderGuideBottomSheet,
    closeSleepDisorderGuideBottomSheet: categoryGuideBottomSheetActions.closeSleepDisorderGuideBottomSheet,
  };
};

export default useTelemedicineRequestViewModel;
